// src/features/auth/components/RegisterForm.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { TextField, Button, Typography, Box, CircularProgress, Paper, Alert, Grid, Link, Checkbox, FormControlLabel, FormHelperText } from '@mui/material';
import { registerUser } from '../api/authApi';
import useAuthStore from '../../../store/authStore';
import clarityService from '../../../services/clarityService';
import { SocialLoginButtons } from '../../../components/auth';
import { OAuthError } from '../../../types/oauth';
import { User } from '../../../services/authService';
import { brand } from '../../../config/brand';

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error } = useAuthStore();

  // Check for app_callback param (desktop/Electron app OAuth flow)
  const searchParams = new URLSearchParams(location.search);
  const appCallback = searchParams.get('app_callback');

  const redirectTo = (location.state as any)?.from?.pathname || '/dashboard';

  /**
   * Redirect to the desktop/Electron app's callback URL with auth credentials.
   */
  const redirectToApp = (user: User) => {
    if (!appCallback) return false;
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return false;
      const userB64 = btoa(JSON.stringify({ id: user.id, email: user.email, firstName: user.firstName, lastName: user.lastName }));
      const separator = appCallback.includes('?') ? '&' : '?';
      window.location.href = `${appCallback}${separator}token=${encodeURIComponent(token)}&user=${encodeURIComponent(userB64)}`;
      return true;
    } catch {
      return false;
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState('');

  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Handle OAuth signup success
  const handleOAuthSuccess = (user: User, isNewUser?: boolean) => {
    // Track successful OAuth signup
    if (isNewUser) {
      clarityService.event('OAuth User Registration Success');
      clarityService.upgrade('New User OAuth Registration');
    } else {
      clarityService.event('OAuth User Login Success');
    }

    if (redirectToApp(user)) return;
    navigate(redirectTo);
  };

  // Handle OAuth signup error
  const handleOAuthError = (error: OAuthError) => {
    clarityService.event('OAuth User Registration Failed');
    setOauthError(error.message);
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Validate form
  const validateForm = (): boolean => {
    let valid = true;
    const newErrors = {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    };
    
    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      valid = false;
    }
    
    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      valid = false;
    }
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
      valid = false;
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      valid = false;
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      valid = false;
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }
    
    // Terms agreement validation
    if (!agreedToTerms) {
      setTermsError('You must agree to the Terms of Use and Privacy Policy');
      valid = false;
    } else {
      setTermsError('');
    }

    setFormErrors(newErrors);
    return valid;
  };

  // Handle registration submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const user = await registerUser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        ...(formData.company.trim() && { company: formData.company.trim() }),
      });

      // Track successful registration event in Clarity
      clarityService.event('User Registration Success');
      clarityService.upgrade('New User Registration');

      if (redirectToApp(user)) return;
      navigate(redirectTo);
    } catch (error) {
      // Track failed registration attempt in Clarity
      clarityService.event('User Registration Failed');

      // Error is handled in the API layer and stored in the auth store
      console.error("Registration failed:", error);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        {`Create Your ${brand.name} Account`}
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="firstName"
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={handleChange}
              error={!!formErrors.firstName}
              helperText={formErrors.firstName}
              disabled={isLoading}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              id="lastName"
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleChange}
              error={!!formErrors.lastName}
              helperText={formErrors.lastName}
              disabled={isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="company"
              label="Company / Organization"
              name="company"
              autoComplete="organization"
              value={formData.company}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="Optional"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
              disabled={isLoading}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              error={!!formErrors.password}
              helperText={formErrors.password}
              disabled={isLoading}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => {
                  setAgreedToTerms(e.target.checked);
                  if (e.target.checked) setTermsError('');
                }}
                color="primary"
                disabled={isLoading}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I have read and agree to the{' '}
                <Link component={RouterLink} to="/terms" target="_blank">
                  Terms of Use
                </Link>{' '}
                and{' '}
                <Link component={RouterLink} to="/privacy" target="_blank">
                  Privacy Policy
                </Link>
              </Typography>
            }
          />
          {termsError && (
            <FormHelperText error sx={{ ml: 2 }}>
              {termsError}
            </FormHelperText>
          )}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 1.5, mb: 2 }}
          disabled={isLoading || !agreedToTerms}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Sign Up'}
        </Button>

        <Box textAlign="center" mt={2}>
          <RouterLink to="/login" style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary">
              Already have an account? Sign in
            </Typography>
          </RouterLink>
        </Box>

        {/* Social Login Options */}
        <SocialLoginButtons
          onSuccess={handleOAuthSuccess}
          onError={handleOAuthError}
          disabled={isLoading}
          context="signup"
          error={oauthError}
        />
      </Box>
    </Paper>
  );
};

export default RegisterForm;